import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Table, Typography } from 'antd';

import { useAccess } from '../../../auth/AccessContext.jsx';
import ActionIcon from '../../../components/ActionIcon.jsx';
import { formatInstant } from '../../../format';
import ApprovalFundFilter from './ApprovalFundFilter.jsx';
import { orderColumns } from './orderColumns.jsx';
import { useOrderList } from './useOrders.js';

const { Text } = Typography;

/** The stored rejected orders, with their reason and a link to the full history. */
export const RejectedTransactionsTable = ({ orders, loading = false, onOpen }) => {
    const columns = [
        ...orderColumns({ onOpen, omit: ['state', 'dealingDate', 'receivedAt', 'unitPrice'] }),
        { title: 'Rejected', dataIndex: 'updatedAt', key: 'updatedAt', width: 170, render: formatInstant },
        {
            title: 'Rejection reason',
            key: 'rejectReason',
            className: 'rejection-reason-cell',
            width: 360,
            render: (_, order) => (
                <>
                    {order.rejectReasonCode && <div><Text strong>{order.rejectReasonCode}</Text></div>}
                    {order.rejectReason ?? <Text type="secondary">No reason recorded</Text>}
                </>
            )
        }
    ];
    return (
        <Table
            className="approval-orders-table rejected-transactions-table"
            rowKey="orderId"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={orders}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            locale={{ emptyText: 'No rejected transactions match' }}
            scroll={{ x: 'max-content' }}
        />
    );
};

/** Read-only audit of Core rejections; these orders remain outside the pending queues. */
const RejectedTransactions = ({ refreshToken, onOpen }) => {
    const { api } = useAccess();
    const [fundId, setFundId] = useState(null);
    const query = useMemo(() => ({ states: ['REJECTED'], ...(fundId ? { fundId } : {}) }), [fundId]);
    const list = useOrderList(api, query);
    const { reload } = list;

    useEffect(() => {
        if (refreshToken) reload();
    }, [refreshToken, reload]);

    return (
        <div className="panel">
            <div className="pending-transactions-toolbar">
                <h3 className="panel-title">Rejected transactions</h3>
                <Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={reload} loading={list.loading}>Refresh</Button>
            </div>
            <div className="rejected-transactions-info">
                <ActionIcon name="info" />
                <span>
                    {list.orders.length}{list.nextCursor ? '+' : ''} rejected transaction(s). Open a transaction to view its rejection reason and history.
                </span>
            </div>
            <div className="pending-transactions-filters">
                <ApprovalFundFilter className="pending-fund-filter" fundId={fundId} onChange={setFundId}
                    seen={list.orders.map((o) => o.fundId)} />
            </div>
            {list.error && <Alert type="error" showIcon title={list.error} className="auth-alert" />}
            <RejectedTransactionsTable orders={list.orders} loading={list.loading} onOpen={onOpen} />
            {list.nextCursor && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                    <Button onClick={list.loadMore} loading={list.loading}>Load more</Button>
                </div>
            )}
        </div>
    );
};

export default RejectedTransactions;
